import type { OnInit } from '@angular/core';
import { Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Eye, EyeOff, Lock, LucideAngularModule, ShieldCheck } from 'lucide-angular';
import { ClientResponseErrorsComponent } from 'src/app/core/components/client-response-errors/client-response-errors.component';
import { PasswordPolicyHintsComponent } from 'src/app/core/components/password-policy-hints/password-policy-hints.component';
import { AccountService } from 'src/app/core/services/account.service';
import { UNEXPECTED_ERROR_MSG } from 'src/app/core/utils/constants';
import {
  passwordMatchValidator,
  passwordPolicyValidator,
} from 'src/app/core/utils/password-policy';
import { AuthService } from '../auth.service';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['../login/login.component.scss', './change-password.component.scss'],
  imports: [
    ReactiveFormsModule,
    ClientResponseErrorsComponent,
    PasswordPolicyHintsComponent,
    LucideAngularModule,
  ],
})
export class ChangePasswordComponent implements OnInit {
  private accountService = inject(AccountService);
  authService = inject(AuthService);
  private router = inject(Router);

  readonly icons = { lock: Lock, eye: Eye, eyeOff: EyeOff, shieldCheck: ShieldCheck };
  readonly username = this.authService.userInfo?.user?.username ?? '';

  form = new FormGroup(
    {
      currentPassword: new FormControl('', { nonNullable: true, validators: Validators.required }),
      newPassword: new FormControl('', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.maxLength(128),
          passwordPolicyValidator(() => this.username),
        ],
      }),
      confirmPassword: new FormControl('', { nonNullable: true, validators: Validators.required }),
    },
    { validators: passwordMatchValidator },
  );

  readonly newPassword = toSignal(this.form.controls.newPassword.valueChanges, {
    initialValue: '',
  });
  showCurrent = signal(false);
  showNew = signal(false);
  submitting = signal(false);
  errorTxt = signal('');

  ngOnInit(): void {
    if (!this.authService.mustChangePassword()) this.router.navigateByUrl('/home');
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.submitting()) return;
    const { currentPassword, newPassword } = this.form.getRawValue();
    this.submitting.set(true);
    this.errorTxt.set('');
    try {
      const response = await firstValueFrom(
        this.accountService.changeMyPassword({ currentPassword, newPassword }),
      );
      // ponytail: WEAK_PASSWORD's `missing` list is already mirrored live by the hints.
      if (response.rtnCode === -1 || !response.response) {
        this.errorTxt.set(response.response?.message ?? UNEXPECTED_ERROR_MSG);
        return;
      }
      await this.authService.completePasswordChange(response.response.token);
      this.router.navigateByUrl('/home');
    } catch (error) {
      this.errorTxt.set(
        (error as { error?: { response?: { message?: string } } })?.error?.response?.message ??
          UNEXPECTED_ERROR_MSG,
      );
    } finally {
      this.submitting.set(false);
    }
  }
}
